import { Bot, CalendarClock, MessageSquare, Plug, ScrollText, ShieldCheck } from "lucide-react"
import { Link } from "react-router-dom"

import { LanguageSwitcher } from "@/components/language-switcher"
import { Button } from "@/components/ui/button"
import { useT } from "@/i18n/provider"
import { useAuth } from "@/lib/auth"

const FEATURES = [
  { icon: Bot, key: "ownAgents" },
  { icon: Plug, key: "ownTools" },
  { icon: MessageSquare, key: "liveChat" },
  { icon: CalendarClock, key: "schedules" },
  { icon: ScrollText, key: "traces" },
  { icon: ShieldCheck, key: "selfHost" },
]

export function LandingPage() {
  const { user } = useAuth()
  const t = useT()

  return (
    <div className="bg-background min-h-full">
      <header className="mx-auto flex max-w-5xl items-center justify-between p-6">
        <div className="flex items-center gap-2">
          <div className="bg-primary text-primary-foreground flex size-8 items-center justify-center rounded-lg">
            <Bot className="size-4" />
          </div>
          <span className="font-semibold">AI Agents</span>
        </div>
        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <Button asChild>
            <Link to={user ? "/chat" : "/login"}>{user ? t("landing.openApp") : t("landing.signIn")}</Link>
          </Button>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-6 pb-16">
        <section className="py-16 text-center">
          <h1 className="mx-auto max-w-2xl text-4xl font-bold tracking-tight">{t("landing.hero.title")}</h1>
          <p className="text-muted-foreground mx-auto mt-4 max-w-2xl text-lg">
            {t("landing.hero.subtitle")}
          </p>
          <div className="mt-8 flex justify-center gap-3">
            <Button size="lg" asChild>
              <Link to={user ? "/chat" : "/login"}>{user ? t("landing.openApp") : t("landing.getStarted")}</Link>
            </Button>
          </div>
        </section>

        <section className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map(({ icon: Icon, key }) => (
            <div key={key} className="rounded-xl border p-5">
              <Icon className="text-primary mb-3 size-5" />
              <h2 className="font-medium">{t(`landing.features.${key}.title`)}</h2>
              <p className="text-muted-foreground mt-1 text-sm">{t(`landing.features.${key}.text`)}</p>
            </div>
          ))}
        </section>

        <section className="mt-12 rounded-xl border p-6">
          <h2 className="text-lg font-semibold">{t("landing.google.title")}</h2>
          <p className="text-muted-foreground mt-2 text-sm leading-6">{t("landing.google.body")}</p>
          <p className="mt-3 text-sm">
            {t("landing.google.privacyBefore")}{" "}
            <Link to="/privacy" className="underline underline-offset-4">
              {t("landing.google.privacyLink")}
            </Link>{" "}
            {t("landing.google.privacyAfter")}
          </p>
        </section>
      </main>

      <footer className="text-muted-foreground mx-auto flex max-w-5xl items-center justify-between border-t p-6 text-sm">
        <span>{t("landing.footer.copyright", { year: new Date().getFullYear() })}</span>
        <Link to="/privacy" className="hover:text-foreground underline underline-offset-4">
          {t("landing.footer.privacy")}
        </Link>
      </footer>
    </div>
  )
}
